<template>
    <div class="columns is-multiline">
        <div class="column is-12">
            <div class="is-flex is-justify-content-space-between is-align-items-center is-flex-wrap-wrap">
                <div class="buttons mb-0">
                    <button class="button is-primary" @click="startNewExtraction">
                        <span class="icon">
                            <i class="fas fa-plus"></i>
                        </span>
                        <span>{{ translate("page.category_manager.start_new_extraction") }}</span>
                    </button>
                </div>
                <span class="tag mt-2-mobile" :class="store.isExtractionLimitReached ? 'is-danger' : 'is-success'">
                    <span class="icon" v-if="store.isExtractionLimitReached">
                        <i class="fas fa-exclamation-triangle"></i>
                    </span>
                    <span class="icon" v-else>
                        <i class="fas fa-check-circle"></i>
                    </span>

                    <span>
                        {{
                            translate("page.category_manager.extraction_count_label", false, [
                                store.extractions.length,
                                MAX_EXTRACTIONS_PER_EVENT,
                            ])
                        }}
                    </span>
                </span>
            </div>
        </div>

        <div class="column is-4">
            <div class="box">
                <h3 class="title is-6 mb-3">
                    {{ translate("page.category_manager.extraction_history_title") }}
                </h3>
                <SelectionList v-model="selectedExtraction" :options="store.extractions" :comparing-function="(a, b) => a.id === b.id">
                    <template v-slot:option="{ option, index }">
                        <div class="is-flex is-align-items-center">
                            <div class="mr-3 is-size-5">#{{ index + 1 }}</div>
                            <div class="is-flex-grow-1">
                                <p class="has-text-weight-bold">
                                    {{ translate("page.category_manager.extraction_started") }}:
                                    {{ formatExtractionDate(option.created) }}
                                </p>
                                <p>
                                    {{ translate("page.category_manager.extraction_updated") }}:
                                    {{ formatExtractionDate(option.updated) }}
                                </p>
                            </div>
                            <span class="tag" :class="getStatusClass(option.status)">
                                {{ translate(`page.category_manager.extraction_status_${option.status}`) }}
                            </span>
                        </div>
                    </template>
                </SelectionList>
            </div>
        </div>

        <div class="column is-8">
            <div class="box extraction-preview">
                <h3 class="title is-6 mb-3">
                    {{ translate("page.category_manager.extraction_result_title") }}
                </h3>

                <!-- Empty extraction result - done but no categories found -->
                <EmptyExtractionState v-if="selectedExtraction && isExtractionEmpty(selectedExtraction)" />

                <!-- Failed extraction -->
                <FailedExtractionState v-else-if="selectedExtraction && isExtractionFailed(selectedExtraction)" />

                <!-- Normal extraction result with categories -->
                <CategoriesPreviewList
                    v-else-if="selectedExtraction"
                    :categories="selectedExtraction.extractedCategories"
                    :loading="false"
                    :show-empty-state="true"
                    :empty-state-message="translate('page.category_manager.extraction_no_result')"
                />

                <!-- No extraction selected -->
                <div v-else class="has-text-centered has-text-grey py-6">
                    {{ translate("page.category_manager.extraction_select_to_view") }}
                </div>
            </div>
        </div>
    </div>

    <hr />

    <div class="buttons is-flex is-justify-content-space-between">
        <button class="button" @click="store.navigateTo(null)">
            <span class="icon">
                <i class="fas fa-arrow-left"></i>
            </span>
            <span>{{ translate("page.category_manager.back_to_options") }}</span>
        </button>

        <LoadingButton class="button is-success" :disabled="!canUseCategories" :is-loading="isImporting" @click="handleUseCategories">
            <span>{{ translate("page.category_manager.use_categories_button") }}</span>
            <span class="icon">
                <i class="fas fa-arrow-right"></i>
            </span>
        </LoadingButton>
    </div>

    <AppendBranchModal
        ref="appendBranchModal"
        :category-count="selectedExtraction?.extractedCategories.length ?? 0"
        :is-loading="isImporting"
        @confirm="importExtractedCategories"
    />
</template>

<script setup lang="ts">
    import SelectionList from "../../Common/SelectionList.vue";
    import CategoriesPreviewList from "../CategoriesPreviewList.vue";
    import EmptyExtractionState from "./EmptyExtractionState.vue";
    import FailedExtractionState from "./FailedExtractionState.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import AppendBranchModal from "../AppendBranchModal.vue";
    import { translate } from "../../Common/translations";
    import { ref, computed } from "vue";
    import type { ComponentExposed } from "vue-component-type-helpers";
    import { MAX_EXTRACTIONS_PER_EVENT, useCategoriesManagerStore } from "../categoriesManagerStore";
    import categoriesExtractionApi, { CategoriesExtractionPreview } from "../../Api/Categories/categoriesExtraction";
    import { convertToTimezone } from "../../Common/dateTimeUtils";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";

    const store = useCategoriesManagerStore();

    const selectedExtraction = ref<CategoriesExtractionPreview | null>(store.extractions[0] || null);

    function startNewExtraction() {
        if (store.isExtractionLimitReached) {
            alert(translate("page.category_manager.extraction_limit_reached_description", false, [MAX_EXTRACTIONS_PER_EVENT]));
            return;
        }
        store.showNewExtractionForm = true;
    }

    function formatExtractionDate(dateString: string): string {
        return convertToTimezone(dateString, store.eventTimezone);
    }

    function getStatusClass(status: string): string {
        switch (status) {
            case "done":
                return "is-success";
            case "failed":
                return "is-danger";
            case "inProgress":
                return "is-warning";
            case "initiated":
                return "is-info";
            default:
                return "";
        }
    }

    function isExtractionEmpty(extraction: CategoriesExtractionPreview): boolean {
        return extraction.status === "done" && extraction.extractedCategories.length === 0;
    }

    function isExtractionFailed(extraction: CategoriesExtractionPreview): boolean {
        return extraction.status === "failed";
    }

    const isImporting = ref(false);

    const canUseCategories = computed(() => {
        return (
            selectedExtraction.value &&
            selectedExtraction.value.status === "done" &&
            selectedExtraction.value.extractedCategories.length > 0
        );
    });

    const appendBranchModal = ref<ComponentExposed<typeof AppendBranchModal> | null>(null);

    function handleUseCategories() {
        if (!selectedExtraction.value || isImporting.value) {
            return;
        }

        if (store.appendMode) {
            appendBranchModal.value?.open(translate("page.category_manager.append_branch_prefill_from_rules"));
            return;
        }

        importExtractedCategories();
    }

    function importExtractedCategories(branchName?: string) {
        if (!selectedExtraction.value || isImporting.value) {
            return;
        }

        isImporting.value = true;

        return categoriesExtractionApi
            .importExtraction(store.eventId, selectedExtraction.value.id, branchName)
            .then(result => {
                successToast(translate("page.category_manager.extraction_import_success", false, [result.importedCategories]));
                return store.refreshCategories();
            })
            .then(() => {
                appendBranchModal.value?.close();
                store.navigateTo("tree-builder");
            })
            .catch(handleError)
            .finally(() => {
                isImporting.value = false;
            });
    }
</script>

<style scoped>
    .extraction-preview {
        height: 55vh;
        display: flex;
        flex-direction: column;
    }
</style>
